/**
 * CourseView — single course page showing modules in sequence
 *
 * Shows course details, ordered module list, and course assessment link.
 * Trainee can click any module (not forced sequential).
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';

interface Module {
  id: string;
  name: string;
  description?: string;
  type: string;
  duration?: number;
}

interface CourseModule {
  module_id: string;
  display_order: number;
  cert_training_modules: Module;
}

interface Assessment {
  id: string;
  name: string;
  passing_score: number;
  time_limit_minutes?: number | null;
}

interface Course {
  id: string;
  name: string;
  description?: string;
  type: string;
  cert_training_course_modules: CourseModule[];
}

interface CourseViewProps {
  courseId: string;
  onSelectModule: (moduleId: string) => void;
  onSelectAssessment: (assessmentId: string) => void;
  onBack: () => void;
  /** When false, hide the "All Courses" back affordance — used when the
   *  visitor arrived via a share link with no list to return to. */
  showBack?: boolean;
  accentColor?: string;
}

function CourseView({ courseId, onSelectModule, onSelectAssessment, onBack, showBack = true, accentColor = '#772cbb' }: CourseViewProps) {
  const { supabase, user } = useAuth();
  const [course, setCourse] = useState<Course | null>(null);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [completedModuleIds, setCompletedModuleIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCourse();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- load on courseId change
  }, [courseId]);

  async function loadCourse() {
    setLoading(true);

    const [courseRes, assessRes] = await Promise.all([
      supabase
        .from('cert_training_courses')
        .select('*, cert_training_course_modules(module_id, display_order, cert_training_modules(id, name, description, type, duration))')
        .eq('id', courseId)
        .single(),
      supabase
        .from('cert_assessments')
        .select('id, name, passing_score, time_limit_minutes')
        .eq('course_id', courseId)
        .eq('active', true),
    ]);

    if (courseRes.data) {
      const sorted = {
        ...courseRes.data,
        cert_training_course_modules: (courseRes.data.cert_training_course_modules || [])
          .sort((a: CourseModule, b: CourseModule) => a.display_order - b.display_order),
      };
      setCourse(sorted as Course);

      // Load completed modules for the current user against THIS course's
      // modules. Drives the tick on each row and the "Continue from here"
      // callout below.
      if (user?.id) {
        const moduleIds = (sorted.cert_training_course_modules as CourseModule[])
          .map(cm => cm.module_id)
          .filter(Boolean);
        if (moduleIds.length > 0) {
          const { data: logRows } = await supabase
            .from('cert_training_log')
            .select('module_id')
            .eq('operator_id', user.id)
            .eq('event_type', 'module_complete')
            .in('module_id', moduleIds);
          setCompletedModuleIds(new Set(((logRows || []) as { module_id: string }[]).map(r => r.module_id)));
        }
      }
    }
    setAssessments(assessRes.data || []);
    setLoading(false);
  }

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-light)' }}>Loading course...</div>;
  }

  if (!course) {
    return <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-light)' }}>Course not found.</div>;
  }

  const modules = course.cert_training_course_modules || [];
  const completedCount = modules.filter(cm => completedModuleIds.has(cm.module_id)).length;
  const allModulesDone = modules.length > 0 && completedCount === modules.length;
  const nextModule = modules.find(cm => !completedModuleIds.has(cm.module_id));

  return (
    <div>
      {/* Back link — hidden for visitors arriving via a share link (no list
          to go back to). */}
      {showBack && (
        <button
          onClick={onBack}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--color-text-light)', fontSize: 'var(--text-base)', padding: '0 0 1rem',
            display: 'flex', alignItems: 'center', gap: '6px',
          }}
        >
          &larr; All Courses
        </button>
      )}

      {/* Course header */}
      <div style={{
        background: 'var(--color-white)', borderRadius: 'var(--radius-lg)', padding: '1.5rem',
        border: '1px solid #e5e7eb', marginBottom: '1.5rem',
      }}>
        <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.375rem', fontWeight: 600 }}>{course.name}</h2>
        {course.description && (
          <p style={{ margin: 0, color: 'var(--color-text-light)', lineHeight: 1.6 }}>{course.description}</p>
        )}
        <div style={{ marginTop: '0.75rem', fontSize: 'var(--text-md)', color: 'var(--color-text-light)' }}>
          {completedCount} of {modules.length} module{modules.length !== 1 ? 's' : ''} complete
          {assessments.length > 0 && ` \u00B7 ${assessments.length} assessment`}
        </div>
        {modules.length > 0 && (
          <div style={{
            marginTop: '0.75rem', height: '6px', background: 'var(--color-bg)',
            borderRadius: '999px', overflow: 'hidden',
          }}>
            <div style={{
              height: '100%',
              width: `${(completedCount / modules.length) * 100}%`,
              background: accentColor,
              transition: 'width 0.3s',
            }} />
          </div>
        )}
      </div>

      {/* Continue / Course complete callout */}
      {allModulesDone ? (
        <div style={{
          background: 'var(--color-success-bg)', border: '1px solid #86efac', borderRadius: 'var(--radius-lg)',
          padding: '1rem 1.25rem', marginBottom: '1rem',
          display: 'flex', alignItems: 'center', gap: '0.75rem',
        }}>
          <span className="text-xl">🎉</span>
          <div>
            <div style={{ fontWeight: 600, color: 'var(--color-success-text)' }}>Course complete</div>
            <div style={{ fontSize: 'var(--text-md)', color: 'var(--color-success-text)', marginTop: '2px' }}>
              All modules done.
              {assessments.length > 0 ? ' Take the course assessment below to finish.' : ''}
            </div>
          </div>
        </div>
      ) : nextModule && nextModule.cert_training_modules && completedCount > 0 ? (
        <div
          onClick={() => onSelectModule(nextModule.module_id)}
          style={{
            background: `${accentColor}10`, border: `1px solid ${accentColor}40`, borderRadius: 'var(--radius-lg)',
            padding: '1rem 1.25rem', marginBottom: '1rem', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '0.75rem',
          }}
        >
          <span className="text-xl">▶</span>
          <div className="flex-1">
            <div style={{ fontWeight: 600, fontSize: 'var(--text-base)', color: accentColor }}>Continue from here</div>
            <div style={{ fontSize: 'var(--text-md)', color: 'var(--color-sidebar-hover)', marginTop: '2px' }}>
              {nextModule.cert_training_modules.name}
            </div>
          </div>
          <span style={{ color: accentColor, fontSize: 'var(--text-3xl)' }}>&rsaquo;</span>
        </div>
      ) : null}

      {/* Module list */}
      <div className="flex flex-col gap-md">
        {modules.map((cm, index) => {
          const mod = cm.cert_training_modules;
          if (!mod) return null;
          const done = completedModuleIds.has(cm.module_id);
          return (
            <div
              key={mod.id}
              onClick={() => onSelectModule(mod.id)}
              style={{
                background: done ? 'var(--color-success-bg)' : 'var(--color-white)',
                borderRadius: 'var(--radius-lg)', padding: '1rem 1.25rem',
                border: `1px solid ${done ? '#86efac' : '#e5e7eb'}`,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '1rem',
                transition: 'box-shadow 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
            >
              {/* Step number — replaced with ✓ when complete */}
              <div style={{
                width: '36px', height: '36px', borderRadius: '50%',
                background: done ? 'var(--color-success)' : `${accentColor}12`,
                color: done ? 'var(--color-white)' : accentColor,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: done ? '1rem' : '0.875rem', flexShrink: 0,
              }}>
                {done ? '✓' : index + 1}
              </div>

              <div className="flex-1">
                <div style={{ fontWeight: 500, fontSize: 'var(--text-lg)' }}>
                  {mod.name}
                  {done && (
                    <span style={{
                      marginLeft: '0.5rem', fontSize: 'var(--text-xs)', fontWeight: 600,
                      color: 'var(--color-success)', background: 'var(--color-success-bg)', padding: '0.125rem 0.5rem',
                      borderRadius: '999px',
                    }}>
                      Completed
                    </span>
                  )}
                </div>
                {mod.description && (
                  <div style={{ fontSize: 'var(--text-md)', color: 'var(--color-text-light)', marginTop: '2px' }}>
                    {mod.description.substring(0, 80)}{mod.description.length > 80 ? '...' : ''}
                  </div>
                )}
              </div>

              {mod.duration && (
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-light)', whiteSpace: 'nowrap' }}>
                  {mod.duration} min
                </div>
              )}

              <span style={{ color: 'var(--color-border)', fontSize: 'var(--text-3xl)' }}>&rsaquo;</span>
            </div>
          );
        })}

        {/* Course assessment(s) */}
        {assessments.map(assess => (
          <div
            key={assess.id}
            onClick={() => onSelectAssessment(assess.id)}
            style={{
              background: 'var(--color-white)', borderRadius: 'var(--radius-lg)', padding: '1rem 1.25rem',
              border: `2px solid ${accentColor}30`, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '1rem',
              transition: 'box-shadow 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
          >
            <div style={{
              width: '36px', height: '36px', borderRadius: '50%',
              background: `${accentColor}20`, color: accentColor,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: 'var(--text-xl)', flexShrink: 0,
            }}>
              &#10003;
            </div>

            <div className="flex-1">
              <div style={{ fontWeight: 600, fontSize: 'var(--text-lg)', color: accentColor }}>
                Course Assessment
              </div>
              <div style={{ fontSize: 'var(--text-md)', color: 'var(--color-text-light)', marginTop: '2px' }}>
                {assess.name} &middot; Pass mark: {assess.passing_score}%
                {assess.time_limit_minutes ? ` \u00B7 ${assess.time_limit_minutes} min limit` : ''}
              </div>
            </div>

            <span style={{ color: accentColor, fontSize: 'var(--text-3xl)' }}>&rsaquo;</span>
          </div>
        ))}
      </div>

      {modules.length === 0 && assessments.length === 0 && (
        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-light)' }}>
          This course is being prepared. Check back soon.
        </div>
      )}
    </div>
  );
}

export default CourseView;
